import * as Sentry from '@sentry/react';
import { useMutation } from '@tanstack/react-query';
import { FC, Fragment, useRef, useState } from 'react';
import PhoneInput, { formatPhoneNumber, isValidPhoneNumber } from 'react-phone-number-input';
import flags from 'react-phone-number-input/flags';

import { useAppContext } from '@contexts/AppContext';

import API from '@api/API';

import {
  useCurrentEntertainerQuery,
  useFetchCurrentEntertainer,
} from '@queries/EntertainerQueries';
import { useCurrentUser } from '@queries/UserQueries';

import { useClaimEntertainerByInstagramHandleMutation } from '@mutations/EntertainerMutations';

import { useAnalytics } from '@hooks/useAnalytics';
import { useCitiesQuery } from '@hooks/useCurrentUserLocation';

import { openExternalUrl } from '@utilities/openExternalUrl';
import { sanitizeInstagramUsername } from '@utilities/sanitizeInstagramUsername';

import Button from '@components/buttons/Button';
import { FormLabel } from '@components/form/FormLabel';
import { Input } from '@components/form/Input';
import { Select } from '@components/form/Select';
import { useDialog } from '@components/layout/Dialog/useDialog';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import { AuthPopupProps } from './LogInScreen';

export const PREFILLED_INSTAGRAM_HANDLE_KEY = 'prefilledInstagramHandle';

export interface AuthPopupEntertainer {
  title: string;
  cityId: string;
  contact: {
    instagram: string;
  };
  details?: {
    categories?: string[];
  };
  entertainerType?: string;
}

export const useQueueEntertainerInstagramMutation = () => {
  const { isPending, mutateAsync } = useMutation({
    mutationFn: async (
      payload: AuthPopupEntertainer & {
        seriesId?: string;
      },
    ) => {
      const response = await API.post<{}, AuthPopupEntertainer>(`/v1/entertainers`, payload);

      return response;
    },
  });

  return {
    isQueuingEntertainerInstagram: isPending,
    queueEntertainerInstagramAsync: mutateAsync,
  };
};

const EntertainerDetailsStep: FC<
  AuthPopupProps & {
    onSubmit: (entertainerProperties: AuthPopupEntertainer) => void;
  }
> = ({ options, setIsEntertainerPopup, onSubmit }) => {
  const { cities = [] } = useCitiesQuery(!options?.cityId);
  const { track } = useAnalytics();
  const { openConfirmDialog } = useDialog();
  const [updatedEntertainerProperties, setUpdatedEntertainerProperties] = useState({
    title: '',
    cityId: options?.cityId,
    contact: {
      instagram:
        sanitizeInstagramUsername(localStorage.getItem(PREFILLED_INSTAGRAM_HANDLE_KEY) ?? '') ?? '',
    },
    entertainerType: options?.entertainerType,
  });

  const handleContinue = () => {
    track('Light Entertainer Onboarding', {
      title: updatedEntertainerProperties.title,
      instagram: updatedEntertainerProperties.contact.instagram,
    });
    onSubmit(updatedEntertainerProperties);
  };

  const handleNonEntertainer = () => {
    track('Click Non-entertainer', {});
    if (options?.allowNonEntertainer) {
      setIsEntertainerPopup(false);
    } else {
      openConfirmDialog({
        title: 'Sorry!',
        text: 'You have to be an entertainer to do this, but you can still checkout local queer events on Gaggl',
        buttonOk: 'See events',
        onConfirm: () => {
          setIsEntertainerPopup(false);
        },
      });
    }
  };

  return (
    <>
      <TitleToolbar
        text={`Step ${options?.authBeforeEntertainerSignup ? 2 : 1} of 2: Entertainer Details`}
        size="md"
      />
      <div className="d-grid gap-2">
        <div>
          <FormLabel text="Stage Name *" />
          <Input
            name="stage-name"
            placeholder="eg. Glitter Disco, Willow Wisp, etc."
            onChange={(e) =>
              setUpdatedEntertainerProperties((prevValue) => ({
                ...prevValue,
                title: e.target.value,
              }))
            }
          />
        </div>
        <div>
          <FormLabel text="Instagram Username *" />
          <Input
            type="text"
            name="instagram"
            placeholder="e.g. your.ig.handle"
            value={updatedEntertainerProperties.contact.instagram}
            onChange={(e) =>
              setUpdatedEntertainerProperties((prevValue) => ({
                ...prevValue,
                contact: { instagram: sanitizeInstagramUsername(e.target.value) },
              }))
            }
          />
        </div>
        {!options?.cityId && (
          <div>
            <FormLabel text="City *" />
            <Select
              name="cityId"
              value={updatedEntertainerProperties.cityId}
              options={[
                { value: '', label: '--' },
                ...cities.map(({ _id, name }) => ({
                  value: _id,
                  label: name,
                })),
              ]}
              onChange={(e) => {
                setUpdatedEntertainerProperties((prevValue) => ({
                  ...prevValue,
                  cityId: e.target.value,
                }));
              }}
            />
          </div>
        )}
        <div>
          <Button
            roundness="rounded"
            onClick={handleContinue}
            disabled={
              !updatedEntertainerProperties ||
              !updatedEntertainerProperties.contact.instagram ||
              !updatedEntertainerProperties.cityId
            }
          >
            Continue
          </Button>
          <Button className="mt-2" variant="flat" color="neutral" onClick={handleNonEntertainer}>
            I'm not an entertainer
          </Button>
        </div>
      </div>
    </>
  );
};

const PhoneNumberStep: FC<AuthPopupProps & { onSubmit: () => void }> = ({ onSubmit, ...props }) => {
  const {
    options,
    authMessage = 'For security, we require a phone number, but no worries, it stays confidential and is never shared.',
    phoneNumber,
    setPhoneNumber,
  } = props;

  return (
    <>
      <TitleToolbar
        text={`Step ${options?.authBeforeEntertainerSignup ? 1 : 2} of 2: Verification`}
        size="md"
      />
      <p className="mt-0">{authMessage}</p>
      <div className="d-grid gap-2">
        <div>
          <FormLabel text="Phone Number *" />
          <PhoneInput
            placeholder="(303) 555-1234"
            defaultCountry="US"
            flags={flags}
            value={phoneNumber}
            onChange={setPhoneNumber}
            inputComponent={Input}
          />
        </div>
        <div>
          <Button
            roundness="rounded"
            onClick={onSubmit}
            disabled={props.isAuthorizing || !isValidPhoneNumber(phoneNumber)}
          >
            Finish
          </Button>
        </div>
        <p className="text-center">
          {'By continuing, you agree to our '}
          <strong onClick={() => openExternalUrl('https://gaggl.app/terms')}>Terms</strong>
          {' and '}
          <strong onClick={() => openExternalUrl('https://gaggl.app/privacy-policy')}>
            Privacy Policy
          </strong>
          .
        </p>
      </div>
    </>
  );
};

export const EntertainerLoginContent: FC<AuthPopupProps> = (props) => {
  const { currentUser } = useCurrentUser();
  const { userIsLoggedIn } = useAppContext();

  const { authBeforeEntertainerSignup = false } = props.options ?? {};

  const entertainerRef = useRef<AuthPopupEntertainer>(null);
  const [step, setStep] = useState<number>(() => {
    let step = 0;

    if (authBeforeEntertainerSignup) {
      step = currentUser?._id || userIsLoggedIn ? 1 : 0;
    }

    return step;
  });

  const handleClaim = async () => {
    if (entertainerRef.current.contact.instagram) {
      await useClaimEntertainerByInstagramHandleMutation.mutationFn({
        title: entertainerRef.current.title,
        instagram: entertainerRef.current.contact.instagram,
        entertainerType: entertainerRef.current.entertainerType,
        phoneNumber: props.phoneNumber ? props.phoneNumber : currentUser?.phone,
        cityId: entertainerRef.current.cityId,
        categories: props.options?.categories,
      });
    }
  };

  const handleSubmitFirstStep = async (entertainerProperties: AuthPopupEntertainer) => {
    try {
      if (!currentUser?._id) {
        setStep((prevStep) => prevStep + 1);
      }
      await props.entertainerCallback(entertainerProperties);
      entertainerRef.current = entertainerProperties;

      if (currentUser?._id) {
        await handleClaim();
        props.onClose();
        props.redirectOverride();
      }
    } catch (e) {
      Sentry.captureException(e);
    }
  };

  const handleSumbitSecondStep = async () => {
    try {
      await props.handleInternalPhoneLogIn();

      if (!authBeforeEntertainerSignup) {
        try {
          await handleClaim();
        } catch (e) {
          Sentry.captureException(e);
        }
      }
    } catch (e) {
      Sentry.captureException(e);
    }
  };

  const STEPS = {
    entertainer: {
      key: 'entertainer',
      render: () => <EntertainerDetailsStep {...props} onSubmit={handleSubmitFirstStep} />,
    },
    phoneNumber: {
      key: 'phoneNumber',
      render: () => <PhoneNumberStep {...props} onSubmit={handleSumbitSecondStep} />,
    },
  };

  const steps = authBeforeEntertainerSignup
    ? [STEPS.phoneNumber, STEPS.entertainer]
    : [STEPS.entertainer, STEPS.phoneNumber];

  return <div>{steps[step]?.render?.()}</div>;
};
